import { mergeAttributes } from '@tencent/exeditor3-core';
import { parseAngleStringToRad, parseAspectRatio, parseCropDetail, stringifyCropDetail, stringifyRadToDegree, styled, calculateDisplayRect, fixFloatNumber, pp, px, } from './utils.js';
const containerClassName = 'exeditor-picture';
const convertRadToDeg = (rotate) => fixFloatNumber(((rotate || 0) / Math.PI) * 180);
const isFixedSize = ({ width, height, aspectRatio, }) => {
    if (width && height) {
        return true;
    }
    // width height 能推算出另一个
    if ((width || height) && aspectRatio) {
        return true;
    }
    return false;
};
const calculateTmpWidth = ({ rotate, width, height, aspectRatio, dataWidth, dataHeight, }) => {
    let tmpWidth;
    let tmpHeight;
    const ar = aspectRatio || 0;
    // NOTE: 必须保证有 width + height 或者 width/height + aspectRatio 中的一个
    const fixedSize = isFixedSize({ width, height, aspectRatio });
    const fixedOriginalSize = isFixedSize({
        width: dataWidth,
        height: dataHeight,
        aspectRatio,
    });
    if (fixedSize) {
        tmpWidth = width || height * ar;
        tmpHeight = height || width / ar;
    }
    else if (fixedOriginalSize) {
        tmpWidth = dataWidth || dataHeight * ar;
        tmpHeight = dataHeight || dataWidth / ar;
    }
    else {
        console.warn('cannot rotate picture without "width" attribute .');
        tmpWidth = width;
        tmpHeight = height;
    }
    const result = calculateDisplayRect({
        width: tmpWidth,
        height: tmpHeight,
        rotate,
    });
    return result.width;
};
const calculatePlaceholderStyles = ({ rotate, crop, aspectRatio, }) => {
    const cropStyles = {
        position: 'absolute',
    };
    const aspectStyles = {
        position: 'relative',
    };
    let viewAspectRatio = aspectRatio;
    if (crop) {
        cropStyles.overflow = 'hidden';
        viewAspectRatio = (crop.width / crop.height) * aspectRatio;
    }
    const result = calculateDisplayRect({
        width: 1,
        height: 1 / viewAspectRatio,
        rotate,
    });
    Object.assign(cropStyles, {
        width: pp(result.realWidth / result.width),
        height: pp(result.realHeight / result.height),
    });
    if (rotate) {
        const deg = convertRadToDeg(rotate);
        Object.assign(cropStyles, {
            'transform-origin': '0 0',
            transform: `rotate(${deg}deg)`,
            left: pp(result.offsetX / result.width),
            top: pp(result.offsetY / result.height),
        });
    }
    aspectStyles['padding-bottom'] = pp(result.height / result.width);
    return [aspectStyles, cropStyles];
};
const calculateImageStyles = ({ crop, }) => {
    const styles = {
        display: 'block',
        width: '100%',
        height: 'auto',
    };
    if (crop) {
        Object.assign(styles, {
            position: 'absolute',
            width: pp(1 / crop.width),
            height: pp(1 / crop.height),
            left: pp(crop.x / crop.width),
            top: pp(crop.y / crop.height),
        });
    }
    return styles;
};
const calculateContainerStyles = ({ width, height, dataWidth, dataHeight, widthPercent, aspectRatio, rotate, }) => {
    const styles = {};
    if ((!widthPercent && !rotate) || !aspectRatio) {
        return styles;
    }
    if (widthPercent) {
        styles.width = pp(widthPercent);
    }
    else if (rotate) {
        const tmpWidth = calculateTmpWidth({
            width,
            height,
            dataWidth,
            dataHeight,
            aspectRatio,
            rotate,
        });
        styles.width = px(tmpWidth);
        styles['max-width'] = '100%';
    }
    return styles;
};
const needAspectRatio = ({ width, height, aspectRatio }) => 
// 有宽高则无需宽高比
!(width && height && aspectRatio === width / height);
export const transformAttrsToDOMRules = (info, options, mergedAttributes) => {
    const { src, width, height, dataWidth, dataHeight, rotate, crop, float, widthPercent, } = info;
    const title = options.hasTitle ? info.title : '';
    let { aspectRatio } = info;
    const isInline = options.inline;
    const wrap = (content) => {
        const titleDOMRule = {
            tag: 'span',
            attributes: {
                class: 'title',
            },
            children: title || '',
        };
        const dom = {
            tag: isInline ? 'span' : 'div',
            attributes: {
                class: containerClassName,
            },
            children: [content],
        };
        if (float) {
            dom.attributes.style = `float: ${float}`;
        }
        if (!isInline && title) {
            dom.children.push(titleDOMRule);
        }
        return {
            ...dom,
            attributes: mergeAttributes(mergedAttributes, dom.attributes),
        };
    };
    const imageAttributes = { src };
    function assignAttribute(name, value, transfer) {
        if (value) {
            imageAttributes[name] = (transfer ? transfer(value) : value);
        }
    }
    if (isInline && width && height) {
        assignAttribute('width', width);
        assignAttribute('height', height);
        assignAttribute('data-rotate', rotate, stringifyRadToDegree);
        assignAttribute('data-crop', crop, stringifyCropDetail);
        const { containerStyles, 
        // placeholderStyles,
        viewStyles, imageStyles, } = calculateFixedSizeStyles({
            width,
            height,
            rotate,
            crop,
        });
        if (!containerStyles) {
            return wrap({
                tag: 'img',
                attributes: imageAttributes,
            });
        }
        // 无占位
        if (!viewStyles) {
            return wrap({
                tag: 'span',
                attributes: { style: styled(containerStyles) },
                children: {
                    tag: 'img',
                    attributes: {
                        style: styled(imageStyles),
                        ...imageAttributes,
                    },
                },
            });
        }
        // 有占位
        return wrap({
            tag: 'span',
            attributes: { style: styled(containerStyles) },
            children: {
                tag: 'span',
                attributes: { style: styled(viewStyles) },
                children: {
                    tag: 'img',
                    attributes: {
                        style: styled(imageStyles),
                        ...imageAttributes,
                    },
                },
            },
        });
    }
    assignAttribute('title', title);
    assignAttribute('width', width);
    assignAttribute('height', height);
    assignAttribute('data-width-percent', widthPercent, pp);
    if (needAspectRatio({ width, height, aspectRatio })) {
        assignAttribute('data-aspect-ratio', aspectRatio, fixFloatNumber);
    }
    if (!aspectRatio && width && height) {
        aspectRatio = width / height;
    }
    const containerStyles = calculateContainerStyles({ ...info, aspectRatio });
    const [placeholderStyles, viewStyles] = calculatePlaceholderStyles({
        rotate,
        crop,
        aspectRatio,
    });
    const imageStyles = calculateImageStyles({ rotate, crop });
    // 原始状态
    if ((!widthPercent && !rotate) || !aspectRatio) {
        return wrap({
            tag: 'img',
            attributes: imageAttributes,
        });
    }
    // 无需占位元素
    if (!rotate && !crop) {
        return wrap({
            tag: 'div',
            attributes: { style: styled(containerStyles) },
            children: {
                tag: 'img',
                attributes: {
                    style: styled(imageStyles),
                    ...imageAttributes,
                },
            },
        });
    }
    const fixedSize = isFixedSize({ width, height, aspectRatio });
    if (!widthPercent && rotate && !fixedSize) {
        assignAttribute('data-width', dataWidth, fixFloatNumber);
        assignAttribute('data-height', dataHeight, fixFloatNumber);
    }
    assignAttribute('data-rotate', rotate, stringifyRadToDegree);
    assignAttribute('data-crop', crop, stringifyCropDetail);
    return wrap({
        tag: 'div',
        attributes: { style: styled(containerStyles) },
        children: {
            tag: 'div',
            attributes: { style: styled(placeholderStyles) },
            children: {
                tag: 'div',
                attributes: { style: styled(viewStyles) },
                children: {
                    tag: 'img',
                    attributes: {
                        style: styled(imageStyles),
                        ...imageAttributes,
                    },
                },
            },
        },
    });
};
const parseSrc = (node) => {
    if (node instanceof HTMLImageElement && node.src) {
        return node.src;
    }
    return node.dataset.src || '';
};
const parseTitle = (node) => {
    if (node.title) {
        return node.title;
    }
    return node.dataset.title || '';
};
const findImageNode = (node) => {
    if (node instanceof HTMLImageElement) {
        return node;
    }
    const found = node.querySelector('img');
    return found || node;
};
const parseFromDataset = (node, parseFn, dataName, defaultValue) => {
    const str = node.dataset[dataName];
    if (str) {
        return parseFn(str);
    }
    return defaultValue;
};
export const parseImageAttrsFromNode = (inputNode) => {
    var _a, _b;
    const node = findImageNode(inputNode);
    const src = parseSrc(node);
    const title = parseTitle(node);
    // basic info
    let width;
    let height;
    if (node instanceof HTMLImageElement) {
        width = node.width;
        height = node.height;
    }
    const dataWidth = parseFromDataset(node, parseFloat, 'width', 0);
    const dataHeight = parseFromDataset(node, parseFloat, 'height', 0);
    const widthPercent = parseFromDataset(node, parseFloat, 'widthPercent', 0) / 100;
    let aspectRatio = parseFromDataset(node, parseAspectRatio, 'aspectRatio', 0);
    const rotate = parseFromDataset(node, parseAngleStringToRad, 'rotate', 0);
    const crop = parseFromDataset(node, parseCropDetail, 'crop', null);
    const display = (_a = node.style) === null || _a === void 0 ? void 0 : _a.display;
    // mdn.io/float
    let float = ((_b = node.style) === null || _b === void 0 ? void 0 : _b.cssFloat) || '';
    if (!width && dataWidth) {
        width = dataWidth;
    }
    if (!height && dataHeight) {
        height = dataHeight;
    }
    if (width && height) {
        aspectRatio = width / height;
    }
    if (display === 'none' || float === 'none') {
        float = '';
    }
    return {
        src,
        title,
        width,
        height,
        rotate,
        crop,
        float,
        widthPercent,
        aspectRatio,
    };
};
export const transformAttrsToActualState = (info, { container, image, lastState, }) => {
    const { rotate, crop, float, widthPercent } = info;
    let { aspectRatio } = info;
    const rect = container.getBoundingClientRect();
    const state = { ...lastState, crop, rotate, float };
    if (!widthPercent) {
        state.widthPercent = 0;
        Object.assign(state, {
            width: image.originWidth,
            height: image.originHeight,
        });
        return state;
    }
    state.widthPercent = widthPercent;
    if (!aspectRatio) {
        aspectRatio = image.originWidth / image.originHeight;
    }
    const viewWidth = rect.width * state.widthPercent;
    state.width = viewWidth;
    state.height = state.width / aspectRatio;
    const a = rotate || 0;
    const sin = Math.abs(Math.sin(a));
    const cos = Math.abs(Math.cos(a));
    // 旋转时保持横向的比例
    if (rotate) {
        state.width = viewWidth / (cos + sin / aspectRatio);
        state.height = state.width / aspectRatio;
    }
    if (crop) {
        const cropAspectRatio = (crop.width / crop.height) * aspectRatio;
        const cropWidth = viewWidth / (cos + sin / cropAspectRatio);
        const cropHeight = cropWidth / cropAspectRatio;
        state.width = cropWidth / crop.width;
        state.height = cropHeight / crop.height;
        state.crop = {
            width: cropWidth,
            height: cropHeight,
            x: state.width * crop.x,
            y: state.height * crop.y,
        };
    }
    return state;
};
export const calculateFixedSizeStyles = ({ width, height, rotate = 0, crop, }) => {
    let containerStyles;
    // let placeholderStyles: PropertiesHyphen;
    let viewStyles;
    let imageStyles;
    // 不需要占位，不需要特殊定位
    if (!rotate && !crop) {
        return {};
    }
    // 不需要占位，使用绝对定位
    if (!rotate && crop) {
        containerStyles = {
            display: 'block',
            position: 'relative',
            width: px(crop.width),
            height: px(crop.height),
            overflow: 'hidden',
        };
        imageStyles = {
            display: 'block',
            position: 'absolute',
            top: px(crop.y),
            left: px(crop.x),
        };
        return {
            containerStyles,
            imageStyles,
        };
    }
    const result = calculateDisplayRect({
        width,
        height,
        rotate,
        crop,
    });
    // 需要占位
    containerStyles = {
        display: 'block',
        position: 'relative',
        width: px(result.width),
        height: px(result.height),
    };
    const deg = convertRadToDeg(rotate);
    // 无裁剪则不需要 overflow 的容器
    imageStyles = {
        display: 'block',
        position: 'absolute',
        left: px(result.offsetX),
        top: px(result.offsetY),
        transform: `rotate(${deg}deg)`,
        'transform-origin': '0 0',
    };
    if (crop) {
        // 增加 overflow 容器
        viewStyles = Object.assign(imageStyles, {
            width: px(result.realWidth),
            height: px(result.realHeight),
            overflow: 'hidden',
        });
        imageStyles = {
            display: 'block',
            position: 'absolute',
            left: px(result.x),
            top: px(result.y),
        };
    }
    return {
        containerStyles,
        // placeholderStyles,
        viewStyles,
        imageStyles,
    };
};
//# sourceMappingURL=transforms.js.map